import { useState } from "react";
import { useAppForm } from "../hooks/useAppForm";
import {
  Stack,
  Textarea,
  Select,
  Button,
  Group,
  Alert,
  Text,
  Rating,
  Box,
} from "@mantine/core";
import {
  IconCheck,
  IconAlertCircle,
  IconMessageCircle,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import apiClient from "../lib/apiClient";

interface FeedbackModalProps {
  onClose: () => void;
}

interface FeedbackFormData {
  category: "bug" | "feature_request" | "general" | "";
  rating: number;
  message: string;
}

export function FeedbackModal({ onClose }: FeedbackModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const form = useAppForm<FeedbackFormData>({
    initialValues: {
      category: "",
      rating: 0,
      message: "",
    },
    validate: {
      category: (value) => (!value ? "Please select a category" : null),
      message: (value) => {
        if (!value || value.trim().length < 10) {
          return "Feedback must be at least 10 characters";
        }
        if (value.length > 500) {
          return "Feedback must be less than 500 characters";
        }
        return null;
      },
    },
  });

  const handleSubmit = async (values: FeedbackFormData) => {
    setIsSubmitting(true);
    try {
      await apiClient.post("/feedback", {
        category: values.category,
        rating: values.rating || null,
        message: values.message,
      });

      setShowSuccess(true);
      form.reset();

      notifications.show({
        title: "Thank you!",
        message: "Your feedback has been submitted successfully.",
        color: "green",
        icon: <IconCheck size={18} />,
      });

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || "Failed to submit feedback";

      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success state
  if (showSuccess) {
    return (
      <Stack gap="lg" align="center" py="xl">
        <IconCheck size={64} color="var(--mantine-color-green-6)" />
        <Text size="xl" fw={600} ta="center">
          Thank you for your feedback!
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          We appreciate you taking the time to help us improve LivingSyncs.
        </Text>
      </Stack>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {/* Privacy Notice */}
        <Alert
          icon={<IconAlertTriangle size={18} />}
          color="yellow"
          variant="light"
          title="Privacy Notice"
        >
          <Text size="sm">
            Please do <strong>not</strong> include any resident information
            (names, medical details, or other Protected Health Information) in
            your feedback. This helps us maintain HIPAA compliance.
          </Text>
        </Alert>

        {/* Intro Text */}
        <Box>
          <Text size="sm" c="dimmed">
            We'd love to hear from you! Your feedback helps us improve
            LivingSyncs for everyone.
          </Text>
        </Box>

        {/* Category */}
        <Select
          label="Feedback Category"
          placeholder="What's this about?"
          required
          data={[
            { value: "bug", label: "🐛 Bug Report" },
            { value: "feature_request", label: "💡 Feature Request" },
            { value: "general", label: "💬 General Feedback" },
          ]}
          {...form.getInputProps("category")}
        />

        {/* Rating */}
        <Box>
          <Text size="sm" fw={500} mb="xs">
            How would you rate your experience? (optional)
          </Text>
          <Rating size="lg" {...form.getInputProps("rating")} />
        </Box>

        {/* Message */}
        <Textarea
          label="Your Feedback"
          placeholder="Tell us what's on your mind..."
          required
          minRows={8}
          maxRows={15}
          autosize
          description={`${form.values.message.length}/500 characters`}
          leftSection={<IconMessageCircle size={16} />}
          {...form.getInputProps("message")}
        />

        {/* Actions */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            leftSection={<IconMessageCircle size={16} />}
          >
            Submit Feedback
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
